import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import request from '../../utils/request';
import { Result } from '../../models/general';
import { isDemoModuleId, isDemoPagePath, isDemoTab, pageTabGroups } from '../../components/layout/navigationModules';
import { subscribeSignalREvent, subscribeSignalRReconnected } from '../../hooks/useSignalRConnection';

/**
 * Module configuration view model from API
 */
export interface ModuleConfigView {
  moduleId: string;
  isEnabled: boolean;
  displayOrder?: number;
}

/**
 * Page configuration view model from API
 */
export interface PageConfigView {
  moduleId: string;
  pagePath: string;
  isEnabled: boolean;
}

/**
 * Input model for updating page configuration
 */
export interface PageConfigInput {
  moduleId: string;
  pagePath: string;
  isEnabled: boolean;
}

/**
 * Company module configuration view model from API (Operator only)
 */
export interface CompanyModuleConfigView {
  companyId: number;
  companyName: string;
  modules: ModuleConfigView[];
  pages: PageConfigView[];
}

/**
 * Input model for updating module configuration
 */
export interface ModuleConfigInput {
  moduleId: string;
  isEnabled: boolean;
  displayOrder?: number;
}

/**
 * One read of a config endpoint, with the thing the old fetchers threw away: whether it worked.
 *
 * `ok` is false on a rejected request, on a body that carries `Result.errors`, AND on a 2xx whose
 * payload is not an array. The last one matters: both GETs answer a server-side exception with a
 * `Result.BadRequest` that has no payload, and `payload ?? []` used to turn that into "this company
 * has no rows", which every consumer read as "everything is enabled" (CO-851).
 */
interface ConfigRead<TRow> {
  rows: TRow[];
  ok: boolean;
}

async function readConfigRows<TRow>(url: string, label: string): Promise<ConfigRead<TRow>> {
  try {
    const response = await request.get<Result<TRow[]>>(url);
    const body = response.data;
    if (body?.errors?.length) {
      console.error(`Failed to fetch ${label}:`, body.errors[0]?.description ?? 'the response carried errors');
      return { rows: [], ok: false };
    }
    const payload: unknown = body?.payload;
    if (!Array.isArray(payload)) {
      console.error(`Failed to fetch ${label}: the response carried no payload`);
      return { rows: [], ok: false };
    }
    return { rows: payload as TRow[], ok: true };
  } catch (error) {
    console.error(`Failed to fetch ${label}:`, error);
    return { rows: [], ok: false };
  }
}

const readModuleConfig = () => readConfigRows<ModuleConfigView>('navigation/modules', 'module config');
const readPageConfig = () => readConfigRows<PageConfigView>('navigation/pages', 'page config');

/**
 * Get module configuration for current user's company.
 * Returns an empty array on error, which cannot be told from "no rows". Runtime consumers (sidebar,
 * route guard, tab strips, dashboard) must read `useNavConfig()` instead, which knows the difference.
 */
export async function getModuleConfig(): Promise<ModuleConfigView[]> {
  return (await readModuleConfig()).rows;
}

/**
 * Get module configuration for a specific company (Operator only).
 */
export async function getCompanyModuleConfig(companyId: number): Promise<CompanyModuleConfigView | null> {
  try {
    const response = await request.get<Result<CompanyModuleConfigView>>(
      `navigation/modules/company/${companyId}`
    );
    return response.data?.payload ?? null;
  } catch (error) {
    console.error('Failed to fetch company module config:', error);
    return null;
  }
}

/**
 * Update module configuration for a company (Operator only).
 */
export async function updateCompanyModuleConfig(
  companyId: number,
  modules: ModuleConfigInput[]
): Promise<boolean> {
  try {
    await request.put(`navigation/modules/company/${companyId}`, modules);
    return true;
  } catch (error) {
    console.error('Failed to update company module config:', error);
    return false;
  }
}

/**
 * Get all companies' module configs (Operator only).
 */
export async function getAllCompanyModuleConfigs(): Promise<CompanyModuleConfigView[]> {
  try {
    const response = await request.get<Result<CompanyModuleConfigView[]>>('navigation/modules/all');
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch all company module configs:', error);
    return [];
  }
}

/**
 * Company picker entry: id and name only.
 *
 * Deliberately NOT `CompanyView` from api/General/Company. The Operator is excluded from company
 * employee data by standing owner decision (CLAUDE.md Role Model), so a cross-tenant picker should
 * carry the narrowest payload that can render it. `navigation/modules/all?summary=true` is that
 * payload: Roles = "Operator" on the server (NavigationController.cs:209), a projection over
 * Companies alone that never touches the config tables, and no employee-scoped column anywhere in
 * the shape.
 */
export type CompanySummary = Pick<CompanyModuleConfigView, 'companyId' | 'companyName'>;

/**
 * Outcome of a company-summaries fetch.
 *
 * 🔴 THE POINT OF THE SHAPE IS THAT `companies: []` IS AMBIGUOUS AND MUST NOT BE THE ONLY RETURN.
 * The previous version of this helper swallowed every failure into `[]`, and its one caller then
 * rendered "No companies found. Only Operators can access this page." — so a 500, a timeout or a
 * dropped connection told an Operator they were not an Operator. `failure` is what separates
 * "the request came back and the list really is empty" from "the request did not come back".
 */
export interface CompanySummariesResult {
  /** Always an array. Empty on failure, and empty is NOT by itself evidence of anything. */
  companies: CompanySummary[];
  /**
   * `null` only when the request completed AND carried no `Result.errors`. Otherwise the reason,
   * for logging and for a caller that wants to show a diagnostic.
   *
   * ⚠️ NOT display copy. It is a server description or a thrown-error message, never localized, so a
   * caller must render its own translated message and keep this for the console/diagnostics.
   */
  failure: string | null;
  /**
   * The TRANSPORT status of a failure, so a caller can tell "you may not do this" (401/403) from
   * "this broke, try again" (5xx, timeout, offline). `null` on success and whenever the status could
   * not be recovered.
   *
   * 🔴 Read from `__httpStatus`, never from the body's own `status`: `Result<TPayload>.Status`
   * defaults to 200 and `Result.Ok<T>()` never sets it, so a body under an HTTP 403 still
   * self-reports 200 (CO-56, requestHandling.ts). ASP.NET's authorization denial is BODILESS, which
   * is exactly the case where the body can tell you nothing.
   */
  status: number | null;
}

/** Transport status of a rejected request, or null. Same contract as `httpStatusOf` in utils/requestHandling.ts. */
function httpStatusOf(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null;
  const own = (error as { __httpStatus?: unknown }).__httpStatus;
  if (typeof own === 'number' && Number.isFinite(own)) return own;
  const nested = (error as { response?: { status?: unknown } }).response?.status;
  return typeof nested === 'number' && Number.isFinite(nested) ? nested : null;
}

/**
 * A loggable reason for a rejection.
 *
 * The interceptor rejects with the PARSED BODY (or a synthetic object), not with an `Error`, so
 * `String(error)` would record the literal "[object Object]" — a catch that fires and records
 * nothing usable is the defect this helper exists to avoid.
 */
function describeRejection(error: unknown, status: number | null): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object') {
    const first = (error as { errors?: Array<{ description?: string }> }).errors?.[0]?.description;
    if (first) return first;
  }
  if (typeof error === 'string' && error) return error;
  return status !== null ? `HTTP ${status}` : 'request rejected with no readable reason';
}

/**
 * Fetch the company list in summary form (Operator only).
 *
 * The one caller is the Module Config screen, through `useCompanySummaries` in
 * `views/Settings/ModuleConfig/useModuleConfigEditor.ts` (CO-865 removed that page's private copy).
 */
export async function getCompanySummaries(): Promise<CompanySummariesResult> {
  try {
    const response = await request.get<Result<CompanySummary[]>>(
      'navigation/modules/all?summary=true'
    );
    const body = response.data;
    if (!body) {
      // A 2xx with no body is not an empty company list; it is a response we cannot read.
      console.error('Failed to fetch company summaries: empty response body');
      return { companies: [], failure: 'empty response body', status: response.status ?? null };
    }
    if (body.errors?.length) {
      // Key Constraint #4 — errors are checked BEFORE payload. A server that answers 200 with an
      // error list was previously read as "zero companies".
      const detail = body.errors[0]?.description || `status ${body.status}`;
      console.error('Failed to fetch company summaries:', detail);
      return { companies: [], failure: detail, status: response.status ?? null };
    }
    return { companies: body.payload ?? [], failure: null, status: null };
  } catch (error) {
    // 🔴 THE CATCH RECORDS THAT IT FIRED — on the console here, and in the returned `failure`,
    // which is what stops the caller from rendering a failure as an empty list.
    const status = httpStatusOf(error);
    const failure = describeRejection(error, status);
    console.error('Failed to fetch company summaries:', status ?? 'no status', failure, error);
    return { companies: [], failure, status };
  }
}

/* ── CO-849 / CO-861: demo-surface grants of one company (Operator only) ──────────────────────────
   For a surface on the server's demo list an ACTIVE GRANT is the only thing that shows it to a
   tenant. The stored module/page row of such a key decides nothing (a stored ON row shows nothing, a
   stored OFF row needs no clearing), so a switch for a demo key must read `isGranted` from here and
   never the config row. The GET returns the whole demo list, each key marked granted or not, so the
   screen needs no client copy of that list. */

/** The three kinds of key on the demo list, as the server spells them. */
export type DemoSurfaceKind = 'page' | 'tab' | 'module';

/** One key of the demo list and whether this company holds an active grant for it. */
export interface DemoSurfaceGrantView {
  surfaceKind: DemoSurfaceKind;
  /** Page path (leading slash), tab id, or module id. */
  surfaceKey: string;
  /** The owning registry id: the module of a page, the tab group of a tab, the module itself. */
  moduleId: string;
  isGranted: boolean;
  /** ISO timestamp (UTC); null when not granted. */
  grantedAtUtc: string | null;
  /** User id of the granting Operator; null when not granted. */
  grantedById: string | null;
  note: string | null;
}

export interface CompanyDemoSurfaceGrantsView {
  companyId: number;
  companyName: string;
  surfaces: DemoSurfaceGrantView[];
}

/** Names one demo surface. `moduleId` is read for a tab only (its tab group id). */
export interface DemoSurfaceRef {
  surfaceKind: DemoSurfaceKind;
  surfaceKey: string;
  moduleId?: string;
}

/** Body of the grant PUT. `note` is optional, at most 500 characters (the server refuses longer). */
export interface DemoSurfaceGrantInput extends DemoSurfaceRef {
  note?: string;
}

/**
 * Outcome of a grant read or write.
 *
 * `ok: false` is its own branch, never an empty list: an empty list would read as "nothing is
 * granted", which is a statement about the tenant that was never read. `reason` is the server's own
 * sentence when it gave one (an unknown key and an over-long note answer 400 with one); `failure` is
 * diagnostic text for the console, never display copy; `status` is the TRANSPORT status, null when
 * the request never got one.
 */
export type CompanyDemoGrantsResult =
  | { ok: true; grants: CompanyDemoSurfaceGrantsView }
  | { ok: false; status: number | null; reason: string | null; failure: string };

const demoGrantsUrl = (companyId: number): string => `navigation/demo-grants/company/${companyId}`;

/** The server's first error description on a rejected request, or null. */
function serverReasonOf(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  return (error as { errors?: Array<{ description?: string }> }).errors?.[0]?.description || null;
}

/** All three grant endpoints answer with the same payload; this reads it or says why it cannot. */
function readDemoGrants(
  body: Result<CompanyDemoSurfaceGrantsView> | null | undefined,
  status: number | null
): CompanyDemoGrantsResult {
  if (!body) return { ok: false, status, reason: null, failure: 'empty response body' };
  // Key Constraint 4: errors before payload.
  if (body.errors?.length) {
    const reason = body.errors[0]?.description || null;
    return { ok: false, status, reason, failure: reason ?? `status ${body.status}` };
  }
  const payload = body.payload;
  if (!payload || !Array.isArray(payload.surfaces)) {
    return { ok: false, status, reason: null, failure: 'response carried no surface list' };
  }
  return { ok: true, grants: payload };
}

function demoGrantsFailure(action: string, error: unknown): CompanyDemoGrantsResult {
  const status = httpStatusOf(error);
  const failure = describeRejection(error, status);
  console.error(`Failed to ${action}:`, status ?? 'no status', failure, error);
  return { ok: false, status, reason: serverReasonOf(error), failure };
}

/** The whole demo list for one company, each key marked granted or not (Operator only). */
export async function getCompanyDemoGrants(companyId: number): Promise<CompanyDemoGrantsResult> {
  try {
    const response = await request.get<Result<CompanyDemoSurfaceGrantsView>>(demoGrantsUrl(companyId));
    return readDemoGrants(response.data, response.status ?? null);
  } catch (error) {
    return demoGrantsFailure('fetch company demo grants', error);
  }
}

/**
 * Grant ONE demo surface to a company (Operator only). Idempotent on the server. Answers with the
 * list as it now stands; the server also tells the tenant's open sessions to re-read their config.
 */
export async function grantCompanyDemoSurface(
  companyId: number,
  input: DemoSurfaceGrantInput
): Promise<CompanyDemoGrantsResult> {
  try {
    const response = await request.put<Result<CompanyDemoSurfaceGrantsView>>(demoGrantsUrl(companyId), input);
    return readDemoGrants(response.data, response.status ?? null);
  } catch (error) {
    return demoGrantsFailure('grant company demo surface', error);
  }
}

/**
 * Revoke ONE demo surface from a company (Operator only). Idempotent on the server; the key travels
 * in the query string, not in a body. Answers with the list as it now stands.
 */
export async function revokeCompanyDemoSurface(
  companyId: number,
  surface: DemoSurfaceRef
): Promise<CompanyDemoGrantsResult> {
  try {
    const params: Record<string, string> = {
      surfaceKind: surface.surfaceKind,
      surfaceKey: surface.surfaceKey,
    };
    if (surface.moduleId) params.moduleId = surface.moduleId;
    const response = await request.delete<Result<CompanyDemoSurfaceGrantsView>>(demoGrantsUrl(companyId), { params });
    return readDemoGrants(response.data, response.status ?? null);
  } catch (error) {
    return demoGrantsFailure('revoke company demo surface', error);
  }
}

/**
 * Get page configuration for current user's company.
 * Returns an empty array on error, which cannot be told from "no rows"; see `getModuleConfig`.
 */
export async function getPageConfig(): Promise<PageConfigView[]> {
  return (await readPageConfig()).rows;
}

/**
 * Get page configuration for a specific company (Operator only).
 */
export async function getCompanyPageConfig(companyId: number): Promise<PageConfigView[]> {
  try {
    const response = await request.get<Result<PageConfigView[]>>(
      `navigation/pages/company/${companyId}`
    );
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch company page config:', error);
    return [];
  }
}

/**
 * Update page configuration for a company (Operator only).
 */
export async function updateCompanyPageConfig(
  companyId: number,
  pages: PageConfigInput[]
): Promise<boolean> {
  try {
    await request.put(`navigation/pages/company/${companyId}`, pages);
    return true;
  } catch (error) {
    console.error('Failed to update company page config:', error);
    return false;
  }
}

/**
 * Reserved module id under which company capabilities persist as page rows. Mirrors how profile tabs
 * reuse this table under PROFILE_TAB_MODULE_ID.
 */
export const CAPABILITIES_MODULE_ID = 'company-capabilities';

export const CAPABILITY_KEYS = [
  'canAuthorEmployees',
  'canAuthorRoles',
  'canAuthorGoals',
  'canAuthorSkills',
  'canAuthorAssets',
] as const;

export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];

/**
 * Shared read of the page-config endpoint for capability consumers.
 *
 * 🔴 This used to keep its OWN promise and drop it as soon as it settled, so every mount of
 * `useCompanyCapabilities` fired a fresh `GET navigation/pages`. On the profile page that is once
 * per TAB SWITCH — each `/profile/:id/<tab>` is a separate route rendering its own `<UserProfile>`
 * wrapper, so the shell unmounts and remounts on every tab — for a response the app already had
 * cached in `getNavConfig`. It now reads that one cache.
 *
 * The "drop on settle so a later mount refetches" it replaced was redundant belt-and-braces:
 * `useCompanyCapabilities` already subscribes to `ModuleConfigUpdated` and calls
 * `clearCapabilityConfig()` on it. So freshness is unchanged; only the refetch-per-mount is gone.
 *
 * CO-851: the cache is now the navigation config store below. An empty answer still means "no
 * capability is denied"; on a failed read this resolves with the last good rows when there are any.
 */
export function getCapabilityConfig(userId: string): Promise<PageConfigView[]> {
  return getNavConfig(userId).then(config => config.pages);
}

/** Ask for a fresh read on the next `getCapabilityConfig()`. Same contract as `clearNavConfig`. */
export function clearCapabilityConfig(): void {
  clearNavConfig();
}

/* ────────────────────────────────────────────────────────────────────────────────────────────────
   CO-851: THE NAVIGATION CONFIG STORE. One read of the company's module + page config, shared by
   every runtime consumer: the sidebar (`useFilteredNavigation`), the route guard (`PageConfigGuard`),
   tab strips (`usePageTabConfig`), the dashboard (`useEnabledModules`) and, through the wrappers at
   the end, `useCompanyCapabilities`.

   WHAT IT REPLACED. Three independent reads (guard, sidebar, dashboard), each of which turned a
   failed request into `[]` and read `[]` as "everything enabled". Demo surfaces are hidden only by
   OFF rows, so one bad response put fabricated pages back in the sidebar, or let them open by URL,
   on a correctly configured tenant, and the sidebar and the guard could disagree until a reload.

   THE RULE, and it lives here so no consumer can apply half of it:
     - a surface WITHOUT the registry's demo marker is off only on an explicit `isEnabled: false`
       row. A failed or empty read leaves it ON, exactly as before: a config outage must never lock
       a tenant out of pages it is entitled to;
     - a surface WITH the marker is on only when the read that governs it succeeded, returned at
       least one row, and no OFF row matches. Pages and tabs are governed by the pages read, modules
       by the modules read, each on its own.
   "Returned at least one row" is deliberate. Every company is created with OFF rows for its demo
   surfaces, so a successful read with zero rows describes a tenant nobody configured, not one that
   was granted everything. A grant must therefore reach the client as rows, never as an empty list.

   Which surfaces carry the marker is asked of the registry on every call (`isDemoPagePath`,
   `isDemoModuleId`, `isDemoTab`). Nothing here knows how many there are.

   A FAILED READ IS NOT CACHED AS A SUCCESS. The last good rows for this user are kept if there are
   any (so a refresh that fails mid-session changes nothing on screen), the status says `failed`, and
   the read is retried with a bounded backoff while the tab is visible.

   ONE OWNER OF INVALIDATION. The store refetches on the `ModuleConfigUpdated` broadcast, when the
   SignalR connection comes back (broadcasts sent while it was down are lost), and when the window
   regains focus after the stale window. Consumers subscribe to nothing.

   🔴 COST (Azure guard 3: on production every SQL command behind these two GETs is a paid telemetry
   row). There is no polling. A healthy session reads once, then only on a broadcast, a reconnect, or
   a refocus more than five minutes after the last good read. Retries exist only while a read is
   failing, stop while the tab is hidden, and slow to one every five minutes.
   ──────────────────────────────────────────────────────────────────────────────────────────────── */

export interface NavConfig {
  modules: ModuleConfigView[];
  pages: PageConfigView[];
}

/**
 * `idle`    nobody is signed in, or nothing has been asked for yet.
 * `loading` the first read for this user is in flight. Nothing is known: demo surfaces are closed.
 * `ready`   the last read succeeded for both lists.
 * `failed`  the last read failed for at least one list. Rows are the last good ones, or empty.
 * A background refresh never goes back to `loading`, so nothing flashes on a broadcast or a refocus.
 */
export type NavConfigStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface NavConfigState {
  /** Who the rows belong to. A consumer must ignore a state whose owner is not its own user. */
  owner: string | null;
  status: NavConfigStatus;
  modules: ModuleConfigView[];
  pages: PageConfigView[];
  /** `modules` came from a successful read for this owner (possibly an earlier one). */
  modulesOk: boolean;
  /** `pages` came from a successful read for this owner (possibly an earlier one). */
  pagesOk: boolean;
  /**
   * When a full good read last REPLACED what is held here, or null. A re-read that returns the same
   * rows publishes nothing (see `applyNavRead`), so this is not "last contacted the server".
   */
  fetchedAt: number | null;
}

const IDLE_NAV_CONFIG: NavConfigState = {
  owner: null,
  status: 'idle',
  modules: [],
  pages: [],
  modulesOk: false,
  pagesOk: false,
  fetchedAt: null,
};

/** Owner key for a signed-in session whose user id has not reached the redux snapshot. */
const SESSION_OWNER = '@session';

/** Waits after the 1st, 2nd, 3rd... consecutive failed read. Past the end, `RETRY_CEILING_MS`. */
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 60_000, 60_000, 60_000, 60_000, 60_000];
const RETRY_CEILING_MS = 300_000;
/** A good read older than this is refreshed when the window regains focus. */
const NAV_CONFIG_STALE_MS = 300_000;
/** A failing read is not retried on refocus sooner than this after the last attempt. */
const REFOCUS_RETRY_GAP_MS = 5_000;

let navState: NavConfigState = IDLE_NAV_CONFIG;
const navListeners = new Set<() => void>();
/** The read in flight, including any re-run queued behind it. */
let navRequest: Promise<NavConfig> | null = null;
/** Something changed while a read was in flight; read once more before settling. */
let navRerun = false;
/** `clearNavConfig()` was called; the next `getNavConfig()` must not answer from memory. */
let navDirty = false;
/** Bumped on every reset so a read that outlives its user is discarded. */
let navEpoch = 0;
let navAutoRefresh = true;
let navRetryTimer: ReturnType<typeof setTimeout> | null = null;
let navFailureCount = 0;
let navLastAttemptAt = 0;
/** When both lists were last read successfully. Drives the refocus stale window. */
let navLastGoodAt = 0;
let navWired = false;

const setNavState = (next: NavConfigState): void => {
  navState = next;
  navListeners.forEach(listener => listener());
};

const toNavConfig = (state: NavConfigState): NavConfig => ({ modules: state.modules, pages: state.pages });

const isDocumentVisible = (): boolean =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

const cancelNavRetry = (): void => {
  if (navRetryTimer) {
    clearTimeout(navRetryTimer);
    navRetryTimer = null;
  }
};

/** One timer at most, never while the tab is hidden; `onNavAttention` picks the retry up again. */
const scheduleNavRetry = (): void => {
  cancelNavRetry();
  if (!navAutoRefresh || !isDocumentVisible()) return;
  const delay = RETRY_DELAYS_MS[navFailureCount - 1] ?? RETRY_CEILING_MS;
  navRetryTimer = setTimeout(() => {
    navRetryTimer = null;
    const owner = navState.owner;
    if (owner && navState.status === 'failed' && isDocumentVisible()) void startNavLoad(owner);
  }, delay);
};

/** Same rows in the same order. The lists are small (one row per module, page or tab). */
const sameRows = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a === b || (a.length === b.length && JSON.stringify(a) === JSON.stringify(b));

const applyNavRead = (
  owner: string,
  modules: ConfigRead<ModuleConfigView>,
  pages: ConfigRead<PageConfigView>
): void => {
  const previous = navState;
  const bothOk = modules.ok && pages.ok;
  const status: NavConfigStatus = bothOk ? 'ready' : 'failed';
  // A failed list keeps what the last good read for this user said; `...Ok` stays as it was.
  // A list that came back unchanged keeps its ARRAY too: consumers key effects on these (the
  // dashboard refetches its data when the module answer changes), so a refresh that changed
  // nothing must not look like a change.
  const nextModules = modules.ok && !sameRows(modules.rows, previous.modules) ? modules.rows : previous.modules;
  const nextPages = pages.ok && !sameRows(pages.rows, previous.pages) ? pages.rows : previous.pages;
  const modulesOk = modules.ok || previous.modulesOk;
  const pagesOk = pages.ok || previous.pagesOk;
  const unchanged =
    previous.status === status &&
    previous.modules === nextModules &&
    previous.pages === nextPages &&
    previous.modulesOk === modulesOk &&
    previous.pagesOk === pagesOk;
  if (!unchanged) {
    setNavState({
      owner,
      status,
      modules: nextModules,
      pages: nextPages,
      modulesOk,
      pagesOk,
      fetchedAt: bothOk ? Date.now() : previous.fetchedAt,
    });
  }
  if (bothOk) {
    navLastGoodAt = Date.now();
    navFailureCount = 0;
    cancelNavRetry();
  } else {
    navFailureCount += 1;
    scheduleNavRetry();
  }
};

/**
 * Read both lists for `owner`. Calls made while a read is in flight do not start a second one: they
 * queue ONE re-run behind it (what is in flight may predate the change that prompted the call) and
 * get the same promise, which resolves after the re-run.
 */
function startNavLoad(owner: string): Promise<NavConfig> {
  if (navRequest) {
    navRerun = true;
    return navRequest;
  }
  const epoch = navEpoch;
  cancelNavRetry();
  const pending = (async (): Promise<NavConfig> => {
    try {
      do {
        navRerun = false;
        navDirty = false;
        navLastAttemptAt = Date.now();
        const [modules, pages] = await Promise.all([readModuleConfig(), readPageConfig()]);
        // Signed out, or another user signed in, while this was in flight: not ours to publish.
        if (epoch !== navEpoch) return { modules: [], pages: [] };
        applyNavRead(owner, modules, pages);
      } while (navRerun);
      return toNavConfig(navState);
    } finally {
      // Released in the same turn the loop ends, so a call arriving next starts a fresh read
      // instead of queueing a re-run nobody will run.
      if (epoch === navEpoch) navRequest = null;
    }
  })();
  // The body cannot reach its `finally` before this line: both reads are awaited first.
  navRequest = pending;
  return pending;
}

const resetNavStore = (): void => {
  navEpoch += 1;
  navRequest = null;
  navRerun = false;
  navDirty = false;
  navFailureCount = 0;
  navLastAttemptAt = 0;
  navLastGoodAt = 0;
  cancelNavRetry();
  if (navState !== IDLE_NAV_CONFIG) setNavState(IDLE_NAV_CONFIG);
};

/** Window focus / tab visible again. Never starts a read while one is in flight. */
const onNavAttention = (): void => {
  const owner = navState.owner;
  if (!owner || !navAutoRefresh || navRequest || !isDocumentVisible()) return;
  const now = Date.now();
  if (navState.status === 'failed') {
    // The backoff timer is dropped while the tab is hidden; coming back resumes it.
    if (navRetryTimer) return;
    if (now - navLastAttemptAt >= REFOCUS_RETRY_GAP_MS) void startNavLoad(owner);
    else scheduleNavRetry();
    return;
  }
  if (navState.status === 'ready' && now - navLastGoodAt >= NAV_CONFIG_STALE_MS) {
    void startNavLoad(owner);
  }
};

/** Attached once for the life of the page; every handler is a no-op while nobody is signed in. */
const wireNavStore = (): void => {
  if (navWired) return;
  navWired = true;
  // An Operator saved this company's config. The company group itself is joined by the shell
  // (`useFilteredNavigationState`), which also keeps the connection alive.
  subscribeSignalREvent('ModuleConfigUpdated', () => {
    void refreshNavConfig();
  });
  subscribeSignalRReconnected(() => {
    if (navAutoRefresh) void refreshNavConfig();
  });
  if (typeof window !== 'undefined') window.addEventListener('focus', onNavAttention);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onNavAttention);
};

/**
 * Point the store at the signed-in user, or at nobody. Same user: nothing happens. A different user,
 * or `null`: everything held for the previous one is dropped first.
 *
 * `autoRefresh: false` is for the Operator, who has no company: both GETs answer "User has no
 * company assigned" every time, so retrying or refreshing on focus would only repeat a refusal.
 * The Operator gets one read, and demo surfaces stay closed for them like any failed read.
 */
export function bindNavConfigUser(owner: string | null, autoRefresh = true): void {
  if (owner === null) {
    resetNavStore();
    return;
  }
  navAutoRefresh = autoRefresh;
  if (!autoRefresh) cancelNavRetry();
  if (navState.owner === owner) return;
  resetNavStore();
  wireNavStore();
  setNavState({ ...IDLE_NAV_CONFIG, owner, status: 'loading' });
  void startNavLoad(owner);
}

/** Read again for the current user, keeping what is on screen until the answer lands. */
export function refreshNavConfig(): Promise<NavConfig> {
  const owner = navState.owner;
  return owner ? startNavLoad(owner) : Promise.resolve({ modules: [], pages: [] });
}

/** `useSyncExternalStore` contract: call `listener` on every change, return the unsubscribe. */
export function subscribeNavConfig(listener: () => void): () => void {
  navListeners.add(listener);
  return () => {
    navListeners.delete(listener);
  };
}

/** The current state. Same object until something changes. Check `owner` before trusting it. */
export function getNavConfigSnapshot(): NavConfigState {
  return navState;
}

/* ── The rule, as pure functions of a state ───────────────────────────────────────────────────── */

/** A demo surface may only open on rows that came from a successful read that returned some. */
const demoReadUsable = (ok: boolean, rows: readonly unknown[]): boolean => ok && rows.length > 0;

/** Module or sub-module id. Off on an explicit OFF row; a demo module also needs a usable read. */
export function isModuleEnabledIn(
  state: Pick<NavConfigState, 'modules' | 'modulesOk'>,
  moduleId: string
): boolean {
  if (state.modules.some(m => m.moduleId === moduleId && !m.isEnabled)) return false;
  return !isDemoModuleId(moduleId) || demoReadUsable(state.modulesOk, state.modules);
}

/**
 * The PAGE row alone, by registry path (rows are matched by path only: CO-496 left two generations
 * of module ids in the table). Off on an explicit OFF row; a demo page also needs a usable read.
 */
export function isPageRowEnabledIn(
  state: Pick<NavConfigState, 'pages' | 'pagesOk'>,
  pagePath: string
): boolean {
  if (state.pages.some(p => p.pagePath === pagePath && !p.isEnabled)) return false;
  return !isDemoPagePath(pagePath) || demoReadUsable(state.pagesOk, state.pages);
}

/** A page together with the module (and sub-module) ids that own it. */
export function isPageEnabledIn(
  state: Pick<NavConfigState, 'modules' | 'modulesOk' | 'pages' | 'pagesOk'>,
  pagePath: string,
  moduleIds: readonly string[] = []
): boolean {
  return isPageRowEnabledIn(state, pagePath) && moduleIds.every(id => isModuleEnabledIn(state, id));
}

/**
 * A tab of a `pageTabGroups` group (rows carry `moduleId` = group id, `pagePath` = tab id).
 * `alwaysEnabled` wins. Otherwise off on an explicit OFF row, the last matching row deciding as it
 * always has; a demo tab also needs a usable pages read.
 */
export function isTabEnabledIn(
  state: Pick<NavConfigState, 'pages' | 'pagesOk'>,
  groupId: string,
  tabId: string
): boolean {
  const tab = pageTabGroups.find(g => g.id === groupId)?.tabs.find(tb => tb.id === tabId);
  if (tab?.alwaysEnabled) return true;
  let row: boolean | undefined;
  for (const p of state.pages) {
    if (p.moduleId === groupId && p.pagePath === tabId) row = p.isEnabled;
  }
  if (row === false) return false;
  return !isDemoTab(groupId, tabId) || demoReadUsable(state.pagesOk, state.pages);
}

export interface UseNavConfigResult {
  /** This user's state. `IDLE` while the store still holds nothing for them. */
  state: NavConfigState;
  /**
   * A read has come back for this user, good or failed, or nobody is signed in. The flag to gate a
   * skeleton on: it turns true on failure too, so nothing waits forever on a config outage.
   */
  settled: boolean;
  isModuleEnabled: (moduleId: string) => boolean;
  isPageRowEnabled: (pagePath: string) => boolean;
  isPageEnabled: (pagePath: string, moduleIds?: readonly string[]) => boolean;
  isTabEnabled: (groupId: string, tabId: string) => boolean;
}

interface NavConfigUserSlice {
  user?: { isLoggedIn?: boolean; userId?: string; role?: string };
}

/**
 * The store, for React. Any number of components may call it: they share one read. The first one
 * mounted for a user starts it, and the result object is stable until the state changes.
 */
export function useNavConfig(): UseNavConfigResult {
  const isLoggedIn = useSelector((s: NavConfigUserSlice) => s.user?.isLoggedIn);
  const userId = useSelector((s: NavConfigUserSlice) => s.user?.userId);
  const role = useSelector((s: NavConfigUserSlice) => s.user?.role);
  const owner = isLoggedIn ? userId || SESSION_OWNER : null;
  const autoRefresh = role !== 'Operator';

  useEffect(() => {
    bindNavConfigUser(owner, autoRefresh);
  }, [owner, autoRefresh]);

  const snapshot = useSyncExternalStore(subscribeNavConfig, getNavConfigSnapshot, getNavConfigSnapshot);
  // Rows held for someone else (the previous user of this browser tab, before the effect above has
  // re-bound the store) are never applied to this user.
  const state = snapshot.owner === owner ? snapshot : IDLE_NAV_CONFIG;

  return useMemo<UseNavConfigResult>(
    () => ({
      state,
      settled: owner === null || state.status === 'ready' || state.status === 'failed',
      isModuleEnabled: moduleId => isModuleEnabledIn(state, moduleId),
      isPageRowEnabled: pagePath => isPageRowEnabledIn(state, pagePath),
      isPageEnabled: (pagePath, moduleIds) => isPageEnabledIn(state, pagePath, moduleIds),
      isTabEnabled: (groupId, tabId) => isTabEnabledIn(state, groupId, tabId),
    }),
    [state, owner]
  );
}

/* ── Promise wrappers, kept for `useCompanyCapabilities` and any caller outside React ─────────── */

/**
 * The config for `userId`, from the store. Resolves with what the store holds once a read has
 * settled: the rows of a good read, the last good rows after a failed one, or empty lists. It never
 * rejects, and it only starts a request when nothing has been read yet or `clearNavConfig()` asked
 * for a fresh answer.
 */
export function getNavConfig(userId: string): Promise<NavConfig> {
  if (navState.owner !== userId) bindNavConfigUser(userId, navAutoRefresh);
  if (navRequest) return navRequest;
  if (navDirty || navState.status === 'idle' || navState.status === 'loading') return startNavLoad(userId);
  return Promise.resolve(toNavConfig(navState));
}

/**
 * The settled config, or `null` if no read has come back for this user yet. Never triggers a fetch.
 * React code should read `useNavConfig()` instead, which also re-renders when the rows change.
 */
export function peekNavConfig(userId: string): NavConfig | null {
  const settled = navState.status === 'ready' || navState.status === 'failed';
  return navState.owner === userId && settled ? toNavConfig(navState) : null;
}

/**
 * Ask for a fresh answer on the next `getNavConfig()`. Does NOT drop what the store holds and does
 * not fetch by itself: wiping the rows would close every demo surface and flash the sidebar skeleton
 * on each broadcast, and fetching here would fire after sign-out, where this is also called. The
 * store already refetches on `ModuleConfigUpdated`; sign-out is handled by `bindNavConfigUser(null)`.
 */
export function clearNavConfig(): void {
  navDirty = true;
}

/**
 * CO-815 — the HR-owned company setting "Chiefs keep employee self-service" (clock-in, timesheet,
 * schedule, payslips). Off by default: a Chief is the company overview role (owner decision 2026-09-28).
 * Server: `GET/PUT api/Navigation/chief-self-service`, policy HrOnly, always the caller's own company.
 */
export interface ChiefSelfServiceView {
  enabled: boolean;
}

export async function getChiefSelfService(): Promise<Result<ChiefSelfServiceView>> {
  const response = await request.get<Result<ChiefSelfServiceView>>('navigation/chief-self-service');
  return response.data;
}

export async function updateChiefSelfService(enabled: boolean): Promise<Result<ChiefSelfServiceView>> {
  const response = await request.put<Result<ChiefSelfServiceView>>('navigation/chief-self-service', { enabled });
  return response.data;
}
